/**
 * @fileoverview Layout Store for Virtual Test Center (VTC)
 * 
 * This Zustand store manages the layout and sizing calculations for the Virtual Test Center.
 * It handles responsive layout for multiple video sessions, calculates optimal grid layouts,
 * and manages references to DOM elements for the room container, content area, and drawer.
 * 
 * The store uses a debounced ResizeObserver to automatically recalculate layouts when the
 * container size changes. The debouncing (150ms) prevents excessive recalculations during
 * rapid resize events, making memoization unnecessary.
 * 
 * @module vtc/stores/layout
 * @requires zustand
 * @requires @instructure/debounce
 * @requires ./session
 * @requires ./app
 * 
 * @example
 * // Import and use the store
 * import useLayoutStore from './stores/layout';
 * 
 * function TestingRoom() {
 *   const sessionDimensions = useLayoutStore((state) => state.sessionDimensions);
 *   const initialize = useLayoutStore((state) => state.initialize);
 *   
 *   useEffect(() => {
 *     initialize({ regions: { roomContainer, roomContent, drawer } });
 *   }, []);
 * }
 */

import { create } from 'zustand';
import { debounce } from '@instructure/debounce';
import useSessionStore from './session';

/**
 * Layout Store
 * 
 * @typedef {Object} LayoutState
 * @property {string} mode - Current layout mode (e.g., 'overview')
 * @property {Object} roomContainer - Container element reference and dimensions
 * @property {React.RefObject} roomContainer.elementRef - React ref to the room container element
 * @property {Object} roomContainer.dimensions - Container dimensions
 * @property {number|null} roomContainer.dimensions.width - Container width in pixels
 * @property {number|null} roomContainer.dimensions.height - Container height in pixels
 * @property {Object} roomContent - Content area element reference
 * @property {React.RefObject} roomContent.elementRef - React ref to the content element
 * @property {Object} drawer - Drawer element reference
 * @property {React.RefObject} drawer.elementRef - React ref to the drawer element
 * @property {Object} room - Room element reference
 * @property {React.RefObject} room.elementRef - React ref to the room element
 * @property {Object} sessionCard - Session card configuration (unused)
 * @property {Object} sessionDimensions - Calculated dimensions for session video elements
 * @property {number} sessionDimensions.width - Session width in pixels
 * @property {number} sessionDimensions.height - Session height in pixels
 * 
 * @typedef {Object} LayoutActions
 * @property {Function} initialize - Initializes the layout store with DOM element references
 * @property {Function} calculateSessionSize - Calculates optimal session grid layout
 * @property {Function} calculateLayoutSizing - Recalculates layout based on current sessions
 */

const useLayoutStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /** @type {string} Current layout mode */
  mode: 'overview',

  /** 
   * @type {Object} Room container element and dimensions
   * @property {React.RefObject} elementRef - React ref to container element
   * @property {Object} dimensions - Container dimensions
   */
  roomContainer: {
    elementRef: null,
    dimensions: { width: null, height: null },
  },

  /** 
   * @type {Object} Room content area element
   * @property {React.RefObject} elementRef - React ref to content element
   */
  roomContent: {
    elementRef: null,
  },

  /** 
   * @type {Object} Drawer element
   * @property {React.RefObject} elementRef - React ref to drawer element
   */
  drawer: {
    elementRef: null,
  },

  /** 
   * @type {Object} Room element
   * @property {React.RefObject} elementRef - React ref to room element
   */
  room: {
    elementRef: null,
  },

  /** @type {Object} Session card configuration (deprecated/unused) */
  sessionCard: {},

  /** 
   * @type {Object} Calculated dimensions for session video elements
   * @property {number} width - Session width in pixels
   * @property {number} height - Session height in pixels
   */
  sessionDimensions: {},

  /** @type {Function|null} Cleanup function for ResizeObserver */
  _resizeObserverCleanup: null,

  /** @type {Function|null} Debounced layout sizing function */
  _debouncedLayoutSizing: null,

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Initializes the layout store with DOM element references
   * 
   * Sets up the layout system by storing references to key DOM elements and
   * creating a ResizeObserver to automatically recalculate layouts when the
   * room content area is resized.
   * 
   * This should be called once when the VTC component mounts.
   * 
   * @function initialize
   * @param {Object} data - Initialization data
   * @param {Object} data.regions - DOM element references
   * @param {React.RefObject} data.regions.roomContainer - Ref to the room container element
   * @param {React.RefObject} data.regions.roomContent - Ref to the room content element
   * @param {React.RefObject} data.regions.drawer - Ref to the drawer element
   * @returns {void}
   * 
   * @example
   * // In a React component
   * const roomContainerRef = useRef();
   * const roomContentRef = useRef();
   * const drawerRef = useRef();
   * 
   * useEffect(() => {
   *   useLayoutStore.getState().initialize({
   *     regions: {
   *       roomContainer: roomContainerRef,
   *       roomContent: roomContentRef,
   *       drawer: drawerRef
   *     }
   *   });
   * }, []);
   */
  initialize: (data) => {
    const localRoomContainer = { ...get().roomContainer };
    const localRoomContent = { ...get().roomContent };
    const localDrawer = { ...get().drawer };

    localRoomContainer.elementRef = data.regions.roomContainer;
    localRoomContent.elementRef = data.regions.roomContent;
    localDrawer.elementRef = data.regions.drawer;

    /**
     * Create debounced layout sizing function
     * Used by ResizeObserver, session transitions, and drawer operations
     * 150ms delay prevents excessive recalculations during rapid changes
     */
    const debouncedLayoutSizing = debounce(() => {
      get().calculateLayoutSizing();
    }, 150);

    set({
      roomContainer: localRoomContainer,
      roomContent: localRoomContent,
      drawer: localDrawer,
      _debouncedLayoutSizing: debouncedLayoutSizing,
    });

    /**
     * ResizeObserver for automatic layout updates
     * Observes both roomContainer and roomContent for comprehensive resize detection
     * 
     * Why observe both?
     * - roomContainer: Catches parent/window resizes
     * - roomContent: Catches drawer toggles and internal layout changes
     * 
     * This ensures we detect all resize scenarios:
     * - Window width/height changes
     * - Drawer open/close
     * - Browser zoom
     * - Fullscreen toggle
     */
    const observer = new ResizeObserver((entries) => {
      // Check if any observed element has valid dimensions
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Only recalculate if dimensions are valid (not 0)
        if (width > 0 && height > 0) {
          debouncedLayoutSizing();
          break; // Only need to trigger once per batch
        }
      }
    });

    // Observe both container and content for comprehensive resize detection
    const roomContainerEl = get().roomContainer.elementRef.current;
    const roomContentEl = get().roomContent.elementRef.current;

    if (roomContainerEl) {
      observer.observe(roomContainerEl);
    }
    if (roomContentEl) {
      observer.observe(roomContentEl);
    }

    // Store cleanup function to prevent memory leaks
    set({ _resizeObserverCleanup: () => observer.disconnect() });
  },

  /**
   * Cleanup function to disconnect ResizeObserver
   * Should be called when the layout store is no longer needed
   * 
   * @function cleanup
   * @returns {void}
   * 
   * @example
   * // Call on component unmount
   * useEffect(() => {
   *   return () => useLayoutStore.getState().cleanup();
   * }, []);
   */
  cleanup: () => {
    get()._resizeObserverCleanup?.();
    set({ _resizeObserverCleanup: null });
  },

  /**
   * Calculates the optimal grid layout for session videos
   * 
   * Uses a grid-fitting algorithm to determine the best arrangement of session
   * videos within the available container space. The algorithm:
   * 1. Tries different column/row combinations
   * 2. Maintains the specified aspect ratio for each video
   * 3. Maximizes the total area used while fitting all sessions
   * 4. Returns the optimal dimensions and grid configuration
   * 
   * Results are memoized for performance - same inputs return cached results.
   * 
   * @function calculateSessionSize
   * @param {number} containerWidth - Available width in pixels
   * @param {number} containerHeight - Available height in pixels
   * @param {number} sessionCount - Number of sessions to display
   * @param {number[]} [aspectRatio=[16, 9]] - Aspect ratio as [width, height]
   * @returns {Object} Optimal layout configuration
   * @returns {number} return.width - Width of each session video in pixels
   * @returns {number} return.height - Height of each session video in pixels
   * @returns {number} return.columns - Number of columns in the grid
   * @returns {number} return.rows - Number of rows in the grid
   * @throws {Error} If sessionCount is less than or equal to 0
   * 
   * @example
   * // Calculate layout for 4 sessions in a 1920x1080 container
   * const layout = useLayoutStore.getState().calculateSessionSize(
   *   1920,  // container width
   *   1080,  // container height
   *   4,     // number of sessions
   *   [16, 9] // aspect ratio
   * );
   * // Returns: { width: 940, height: 529, columns: 2, rows: 2 }
   * 
   * @example
   * // Calculate layout for 6 sessions with 4:3 aspect ratio
   * const layout = useLayoutStore.getState().calculateSessionSize(
   *   1600, 900, 6, [4, 3]
   * );
   * // Returns optimal 3x2 or 2x3 grid based on best fit
   */
  calculateSessionSize: (containerWidth, containerHeight, sessionCount, aspectRatio = [16, 9]) => {
    if (sessionCount <= 0) {
      throw new Error('Session count must be greater than 0');
    }

    const aspectW = aspectRatio[0];
    const aspectH = aspectRatio[1];

    let bestCols = 1;
    let bestRows = sessionCount;
    let bestWidth = 0;
    let bestHeight = 0;
    let bestFit = 0;

    for (let cols = 1; cols <= sessionCount; cols++) {
      let rows = Math.ceil(sessionCount / cols);

      // Calculate width-first session size while maintaining aspect ratio
      let sessionWidth = Math.floor(containerWidth / cols);
      let sessionHeight = Math.floor((sessionWidth * aspectH) / aspectW); // Maintain aspect ratio

      // If it exceeds the container height, adjust using height-first approach
      if (sessionHeight * rows > containerHeight) {
        sessionHeight = Math.floor(containerHeight / rows);
        sessionWidth = Math.floor((sessionHeight * aspectW) / aspectH); // Adjust width to maintain aspect ratio
      }

      let totalUsedWidth = sessionWidth * cols;
      let totalUsedHeight = sessionHeight * rows;

      // Ensure it fully fits inside the container and maximizes area usage
      if (
        totalUsedWidth <= containerWidth &&
        totalUsedHeight <= containerHeight
      ) {
        let fitArea = totalUsedWidth * totalUsedHeight;

        if (fitArea > bestFit) {
          bestFit = fitArea;
          bestCols = cols;
          bestRows = rows;
          bestWidth = sessionWidth;
          bestHeight = sessionHeight;
        }
      }
    }

    return {
      width: Math.floor(bestWidth * 0.99),
      height: Math.floor(bestHeight * 0.99),
      columns: bestCols,
      rows: bestRows,
    };
  },

  setDrawerRef: (element) => {
    set((state) => ({
      drawer: {
        ...state.drawer,
        elementRef: { current: element },
      },
    }));
  },

  /**
   * Recalculates and updates the layout based on current sessions
   * 
   * This method:
   * 1. Gets the current dimensions of the room content area
   * 2. Counts the number of sessions currently in testing
   * 3. Calculates the optimal layout using calculateSessionSize
   * 4. Updates the store with the new session dimensions
   * 
   * This is called automatically by the ResizeObserver when the container
   * size changes, and manually when sessions are added/removed from testing.
   * 
   * @function calculateLayoutSizing
   * @returns {void}
   * 
   * @example
   * // Manually trigger layout recalculation
   * useLayoutStore.getState().calculateLayoutSizing();
   * 
   * @example
   * // Called automatically when:
   * // - Container is resized (via ResizeObserver)
   * // - Session added to testing (via session store)
   * // - Session removed from testing (via session store)
   */
  calculateLayoutSizing: () => {
    return;

    const roomContainer = get().roomContainer;
    const localRoomContainer = { ...get().roomContainer };

    // Safety check: Ensure roomContent element is initialized
    const roomContentElement = get().roomContent.elementRef?.current;
    if (!roomContentElement) {
      console.warn('[LayoutStore] calculateLayoutSizing called before roomContent element is initialized');
      return;
    }

    const roomDimensions = roomContentElement.getBoundingClientRect();

    const sessionCount =
      useSessionStore.getState().visibleSessions?.length || 0;

    // now, if the focused session is part of the visible sessions, we need to subtract 1.
    const focusedSessionSid = useSessionStore.getState().focusedSessionSid;
    const visibleSessions = useSessionStore.getState().visibleSessions || [];

    let adjustedSessionCount = sessionCount;
    if (focusedSessionSid && visibleSessions.some(session => session.sessionSid === focusedSessionSid)) {
      adjustedSessionCount = Math.max(0, sessionCount - 1);
    }

    if (adjustedSessionCount > 0) {
      const sessionSize = get().calculateSessionSize(
        roomDimensions.width,
        roomDimensions.height,
        adjustedSessionCount
      );

      localRoomContainer.dimensions = {
        width: roomDimensions.width,
        height: roomDimensions.height,
      };
      set({
        sessionDimensions: {
          width: sessionSize.width,
          height: sessionSize.height,
        },
        roomContainer: localRoomContainer,
      });
    }
  },

  /**
   * Debounced version of calculateLayoutSizing
   * Use this for session transitions and drawer operations to prevent excessive calculations
   * 
   * @function debouncedLayoutUpdate
   * @returns {void}
   * 
   * @example
   * // Use for session transitions
   * useLayoutStore.getState().debouncedLayoutUpdate();
   */
  debouncedLayoutUpdate: () => {
    const debouncedFn = get()._debouncedLayoutSizing;
    if (debouncedFn) {
      debouncedFn();
    } else {
      // Fallback if debounced function not initialized yet
      get().calculateLayoutSizing();
    }
  },
}));

export default useLayoutStore;

// ============================================================================
// LAYOUT ALGORITHM DOCUMENTATION
// ============================================================================

/**
 * @overview Grid Layout Algorithm
 * 
 * The layout algorithm optimizes the arrangement of video sessions in a grid
 * to maximize screen space usage while maintaining aspect ratios.
 * 
 * **Algorithm Steps:**
 * 
 * 1. **Iterate through possible column counts** (1 to sessionCount)
 *    - For each column count, calculate the required number of rows
 * 
 * 2. **Calculate session dimensions using width-first approach**
 *    - Divide container width by columns to get session width
 *    - Calculate height based on aspect ratio: `height = (width * aspectH) / aspectW`
 * 
 * 3. **Check if height exceeds container**
 *    - If total height (height × rows) > container height:
 *      - Switch to height-first approach
 *      - Divide container height by rows to get session height
 *      - Calculate width based on aspect ratio: `width = (height * aspectW) / aspectH`
 * 
 * 4. **Validate fit and calculate area**
 *    - Ensure total width and height fit within container
 *    - Calculate total area used: `area = (width × cols) × (height × rows)`
 * 
 * 5. **Track best configuration**
 *    - Keep the configuration with the largest area
 *    - This maximizes screen space usage
 * 
 * 6. **Apply 99% scaling**
 *    - Reduce final dimensions by 1% to account for gaps/padding
 *    - Prevents overflow issues with borders and margins
 * 
 * **Example Calculations:**
 * 
 * ```
 * Container: 1920×1080, Sessions: 4, Aspect: 16:9
 * 
 * Try 1 column:
 *   - 1 col × 4 rows
 *   - Width: 1920 / 1 = 1920
 *   - Height: (1920 × 9) / 16 = 1080
 *   - Total height: 1080 × 4 = 4320 ❌ (exceeds 1080)
 *   - Switch to height-first:
 *     - Height: 1080 / 4 = 270
 *     - Width: (270 × 16) / 9 = 480
 *   - Area: 480 × 1080 = 518,400
 * 
 * Try 2 columns:
 *   - 2 cols × 2 rows
 *   - Width: 1920 / 2 = 960
 *   - Height: (960 × 9) / 16 = 540
 *   - Total height: 540 × 2 = 1080 ✅
 *   - Area: 1920 × 1080 = 2,073,600 ⭐ BEST
 * 
 * Try 3 columns:
 *   - 3 cols × 2 rows
 *   - Width: 1920 / 3 = 640
 *   - Height: (640 × 9) / 16 = 360
 *   - Total height: 360 × 2 = 720 ✅
 *   - Area: 1920 × 720 = 1,382,400
 * 
 * Try 4 columns:
 *   - 4 cols × 1 row
 *   - Width: 1920 / 4 = 480
 *   - Height: (480 × 9) / 16 = 270
 *   - Total height: 270 × 1 = 270 ✅
 *   - Area: 1920 × 270 = 518,400
 * 
 * Result: 2×2 grid with 960×540 sessions (scaled to 950×534)
 * ```
 * 
 * **Performance Optimization:**
 * 
 * The algorithm runs on every resize, but this is acceptable because:
 * - The calculation is fast (simple loop, ~1ms for typical session counts)
 * - ResizeObserver is debounced (150ms), limiting how often it runs
 * - No memoization needed - debouncing provides sufficient optimization
 * 
 * **Edge Cases:**
 * 
 * - **1 session**: Always uses full container (respecting aspect ratio)
 * - **Many sessions**: May result in small videos, but maximizes visible sessions
 * - **Wide containers**: Tends toward more columns
 * - **Tall containers**: Tends toward more rows
 * - **Non-standard aspect ratios**: Algorithm adapts automatically
 */

/**
 * @overview Responsive Layout System
 * 
 * The layout system automatically adapts to container size changes and
 * session count changes using a combination of ResizeObserver and
 * Zustand state management.
 * 
 * **Component Lifecycle:**
 * 
 * ```
 * 1. Component Mount
 *    ↓
 * 2. initialize() called with DOM refs
 *    ↓
 * 3. ResizeObserver attached to roomContent
 *    ↓
 * 4. Initial calculateLayoutSizing() triggered
 *    ↓
 * 5. Layout calculated and stored
 *    ↓
 * 6. Components re-render with new dimensions
 * ```
 * 
 * **Automatic Recalculation Triggers:**
 * 
 * 1. **Container Resize** (via ResizeObserver)
 *    - Window resize
 *    - Drawer open/close
 *    - Browser zoom
 *    - Fullscreen toggle
 * 
 * 2. **Session Count Change** (via session store)
 *    - Session added to testing
 *    - Session removed from testing
 *    - Session transferred
 *    - Session ended
 * 
 * **Data Flow:**
 * 
 * ```
 * Container Resize
 *   ↓
 * ResizeObserver fires
 *   ↓
 * calculateLayoutSizing()
 *   ↓
 * Get container dimensions
 *   ↓
 * Count testing sessions
 *   ↓
 * calculateSessionSize(width, height, count)
 *   ↓
 * Update sessionDimensions in store
 *   ↓
 * Components re-render with new sizes
 *   ↓
 * Videos resize smoothly
 * ```
 * 
 * **Integration with Session Store:**
 * 
 * The layout store reads from the session store to get the count of
 * sessions currently in testing:
 * 
 * ```javascript
 * const sessionCount = Object.keys(
 *   useSessionStore.getState().testing
 * ).length;
 * ```
 * 
 * When sessions transition to/from testing, the session store calls:
 * 
 * ```javascript
 * useAppStore.getState().calculateLayoutSizing();
 * ```
 * 
 * This ensures the layout updates immediately when sessions change.
 */

/**
 * @overview Usage Patterns
 * 
 * **Basic Setup:**
 * 
 * ```jsx
 * function TestingRoom() {
 *   const roomContainerRef = useRef();
 *   const roomContentRef = useRef();
 *   const drawerRef = useRef();
 *   
 *   const sessionDimensions = useLayoutStore(
 *     (state) => state.sessionDimensions
 *   );
 *   
 *   useEffect(() => {
 *     useLayoutStore.getState().initialize({
 *       regions: {
 *         roomContainer: roomContainerRef,
 *         roomContent: roomContentRef,
 *         drawer: drawerRef
 *       }
 *     });
 *   }, []);
 *   
 *   return (
 *     <div ref={roomContainerRef}>
 *       <div ref={roomContentRef}>
 *         {sessions.map(session => (
 *           <SessionVideo
 *             key={session.id}
 *             width={sessionDimensions.width}
 *             height={sessionDimensions.height}
 *           />
 *         ))}
 *       </div>
 *       <div ref={drawerRef}>Drawer content</div>
 *     </div>
 *   );
 * }
 * ```
 * 
 * **Manual Recalculation:**
 * 
 * ```javascript
 * // Force recalculation (rarely needed)
 * useLayoutStore.getState().calculateLayoutSizing();
 * ```
 * 
 * **Custom Aspect Ratio:**
 * 
 * ```javascript
 * // Calculate with 4:3 aspect ratio
 * const layout = useLayoutStore.getState().calculateSessionSize(
 *   1920, 1080, 6, [4, 3]
 * );
 * ```
 * 
 * **Accessing Container Dimensions:**
 * 
 * ```javascript
 * const { width, height } = useLayoutStore(
 *   (state) => state.roomContainer.dimensions
 * );
 * ```
 * 
 * **Performance Considerations:**
 * 
 * 1. **No Memoization Needed**: `calculateSessionSize` runs fresh each time
 *    - Calculation is fast (~1ms)
 *    - Debouncing prevents excessive calls
 * 
 * 2. **Selective Re-renders**: Use Zustand selectors
 *    ```javascript
 *    // Only re-render when dimensions change
 *    const dimensions = useLayoutStore(
 *      (state) => state.sessionDimensions
 *    );
 *    ```
 * 
 * 3. **ResizeObserver**: Efficient browser API
 *    - No polling required
 *    - Fires only on actual size changes
 * 
 * 4. **Debouncing**: ResizeObserver callback is debounced
 *    ```javascript
 *    const debouncedResize = debounce(
 *      () => get().calculateLayoutSizing(),
 *      150  // 150ms delay for smooth UX
 *    );
 *    ```
 *    This prevents excessive recalculations during rapid resize events.
 */

/**
 * @overview Known Limitations
 * 
 * 1. **Fixed Aspect Ratio**
 *    - Currently hardcoded to 16:9
 *    - Could be made configurable per session
 * 
 * 2. **No Gap Configuration**
 *    - 1% reduction is hardcoded
 *    - Could accept gap parameter
 * 
 * 3. **Single Container**
 *    - Assumes all sessions in one container
 *    - Multi-container layouts not supported
 * 
 * 4. **No Minimum Size**
 *    - Videos can become very small with many sessions
 *    - Could add minimum dimension constraints
 * 
 * 5. **ResizeObserver Cleanup**
 *    - Observer is created but never explicitly disconnected
 *    - Should add cleanup in component unmount
 * 
 * **Potential Improvements:**
 * 
 * ```javascript
 * // Add minimum size constraint
 * if (bestWidth < 200 || bestHeight < 150) {
 *   // Use scrolling instead of shrinking
 * }
 * 
 * // Add gap parameter
 * calculateSessionSize(width, height, count, aspectRatio, gap = 8)
 * 
 * // Add cleanup
 * const observer = new ResizeObserver(...);
 * return () => observer.disconnect();
 * ```
 */